/**
 * @package   EGI / Frontend — creator portfolio
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-312)
 * @date      2026-06-19
 * @purpose   Isola Vanilla TS load-more della griglia portfolio /creator/{slug}/portfolio.
 *            (mirra resources/ts/home/carousel-pager.ts): il render iniziale materializza
 *            solo un batch piccolo (PORTFOLIO_PAGE_SIZE=4 opere, M-EGI-313); auto-load LAZY
 *            allo scroll via IntersectionObserver + fallback click su [data-portfolio-loadmore-btn] → fetcha
 *            il partial same-origin {endpoint}?partial=1&mode&offset&view&sort&query&collection,
 *            estrae le card dei blocchi [data-portfolio-grid] del partial e le appende ai
 *            corrispondenti container già nel DOM, poi avanza l'offset leggendolo dal NUOVO
 *            trigger restituito dal server (fonte di verità di offset/hasMore). Esaurite le
 *            opere → bottone nascosto + messaggio "fine risultati" (role=status).
 *            Un solo fetch in volo (aria-busy). Errore → messaggio i18n visibile + retry,
 *            mai console-only. Le card iniettate ereditano i listener delegati su document
 *            (egi-card-interactions.ts). Re-bind delegato su #content-container: sopravvive
 *            ai replace AJAX del cambio tab/mode (home-spa.blade.php). Nessun trigger nel DOM
 *            → no-op totale.
 */

interface LoadMoreState {
    busy: boolean;
}

/** Frammento HTML del NOSTRO endpoint same-origin (Blade-escapato server-side): non input utente raw → no XSS terze parti (CWE-79 n/a). */
function parsePartial(html: string): Document {
    return new DOMParser().parseFromString(html, 'text/html');
}

/**
 * Appende le card di ogni blocco griglia del partial ai container già montati nel DOM.
 * Restituisce true se almeno una card è stata aggiunta (slice non vuota).
 */
function appendCards(partial: Document, container: HTMLElement): boolean {
    const incomingGrids = partial.querySelectorAll<HTMLElement>('[data-portfolio-grid]');
    if (incomingGrids.length === 0) {
        return false;
    }
    let added = 0;
    incomingGrids.forEach((incoming) => {
        const variant = incoming.dataset.portfolioGrid ?? '';
        const target = container.querySelector<HTMLElement>(`[data-portfolio-grid="${variant}"]`);
        if (!target) {
            return; // griglia non presente (es. view diversa) → ignora questo blocco
        }
        Array.from(incoming.children).forEach((card) => {
            target.appendChild(card);
            added += 1;
        });
    });
    return added > 0;
}

function showError(trigger: HTMLElement, message: string, onRetry: () => void): void {
    let box = trigger.querySelector<HTMLElement>('[data-portfolio-loadmore-error]');
    if (!box) {
        box = document.createElement('div');
        box.setAttribute('data-portfolio-loadmore-error', '');
        box.setAttribute('role', 'status');
        box.className = 'flex items-center gap-3 text-sm';
        trigger.appendChild(box);
    }
    box.replaceChildren();
    const span = document.createElement('span');
    span.className = 'text-red-300';
    span.textContent = message;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className =
        'rounded px-3 py-1 font-semibold text-white underline underline-offset-2 hover:text-purple-300 ' +
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-400';
    retry.textContent = trigger.dataset.retryLabel ?? 'Retry';
    retry.addEventListener('click', () => {
        box?.replaceChildren();
        onRetry();
    });
    box.append(span, retry);
}

function buildUrl(trigger: HTMLElement): string {
    const endpoint = trigger.dataset.endpoint ?? '';
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('partial', '1');
    url.searchParams.set('mode', trigger.dataset.mode ?? 'created');
    url.searchParams.set('offset', trigger.dataset.offset ?? '0');
    url.searchParams.set('view', trigger.dataset.view ?? 'grid');
    url.searchParams.set('sort', trigger.dataset.sort ?? 'latest');
    const q = trigger.dataset.query ?? '';
    if (q !== '') {
        url.searchParams.set('query', q);
    }
    const collection = trigger.dataset.collection ?? '';
    if (collection !== '') {
        url.searchParams.set('collection', collection);
    }
    return url.toString();
}

/** Allinea il trigger nel DOM allo stato del trigger restituito dal server (offset/hasMore). */
function syncTriggerFromPartial(trigger: HTMLElement, partial: Document): void {
    const next = partial.querySelector<HTMLElement>('[data-portfolio-loadmore]');
    if (next && next.dataset.offset) {
        trigger.dataset.offset = next.dataset.offset;
        return;
    }
    // Nessun nuovo trigger nel partial → set esaurito: nascondi bottone, mostra "fine".
    const button = trigger.querySelector<HTMLElement>('[data-portfolio-loadmore-btn]');
    button?.classList.add('hidden');
    const end = trigger.querySelector<HTMLElement>('[data-portfolio-loadmore-end]');
    end?.classList.remove('hidden');
    trigger.dataset.exhausted = 'true';
}

function bindLoadMore(container: HTMLElement, state: LoadMoreState): void {
    if (container.dataset.loadmoreBound === 'true') {
        return;
    }
    container.dataset.loadmoreBound = 'true';

    container.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const button = target.closest<HTMLElement>('[data-portfolio-loadmore-btn]');
        if (!button) {
            return;
        }
        event.preventDefault();
        const trigger = button.closest<HTMLElement>('[data-portfolio-loadmore]');
        if (!trigger || trigger.dataset.exhausted === 'true') {
            return;
        }
        void loadMore(container, trigger, button, state);
    });
}

async function loadMore(
    container: HTMLElement,
    trigger: HTMLElement,
    button: HTMLElement,
    state: LoadMoreState,
): Promise<void> {
    if (state.busy) {
        return; // un solo fetch in volo
    }
    state.busy = true;
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.style.opacity = '0.6';
    try {
        const response = await fetch(buildUrl(trigger), {
            headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
            credentials: 'same-origin',
        });
        if (!response.ok) {
            throw new Error('HTTP ' + String(response.status));
        }
        const partial = parsePartial(await response.text());
        appendCards(partial, container);
        syncTriggerFromPartial(trigger, partial);
    } catch {
        showError(trigger, trigger.dataset.errorLabel ?? 'Error', () => {
            void loadMore(container, trigger, button, state);
        });
    } finally {
        state.busy = false;
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-disabled');
        button.style.opacity = '1';
    }
}

/**
 * M-EGI-313: auto-load LAZY allo scroll (come /home). Il render iniziale SSR è un batch
 * piccolo (8 card); il resto si carica quando il trigger entra nel viewport (rootMargin
 * 600px = prefetch poco prima). Il bottone resta come fallback (no-JS / a11y / click).
 */
function autoLoadOnScroll(container: HTMLElement, state: LoadMoreState): void {
    if (!('IntersectionObserver' in window)) {
        return; // niente IO → resta il fallback a click
    }
    const io = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (!entry.isIntersecting) {
                    continue;
                }
                const trigger = entry.target as HTMLElement;
                if (trigger.dataset.exhausted === 'true') {
                    io.unobserve(trigger);
                    continue;
                }
                const button = trigger.querySelector<HTMLElement>('[data-portfolio-loadmore-btn]');
                if (button && !state.busy) {
                    void loadMore(container, trigger, button, state);
                }
            }
        },
        { rootMargin: '600px 0px' },
    );
    const observe = (): void => {
        const trigger = container.querySelector<HTMLElement>('[data-portfolio-loadmore]');
        if (trigger && trigger.dataset.exhausted !== 'true') {
            io.observe(trigger);
        }
    };
    observe();
    // Il cambio tab/mode rimpiazza il contenuto di #content-container → ri-osserva il nuovo trigger.
    new MutationObserver(observe).observe(container, { childList: true, subtree: true });
}

function boot(): void {
    const container = document.getElementById('content-container');
    if (!container) {
        return; // pagina senza shell portfolio → no-op
    }
    // Delegazione su #content-container: il trigger può non esistere al boot (pagina senza
    // altre opere) o essere ri-iniettato dal cambio tab/mode → il listener resta valido.
    const state: LoadMoreState = { busy: false };
    bindLoadMore(container, state);
    autoLoadOnScroll(container, state);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
    boot();
}

export {};
